import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import {
  PartnerIncomeTaxPartnerDto,
  ReturnSummaryDto,
} from '@interfaces/partner-income-tax/responses/partner-income-tax.response';
import { VaultDatePipe } from '@src/app/shared/pipes/vault-date.pipe';
import {
  FINDING_LEVEL_TAGS,
  visibleFindings,
  describeReturnKind,
  formatPersonName,
  formatRatio,
  formatSharePercent,
} from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/utils/partner-income-tax-format.util';
import { ReturnTimelineComponent } from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/components/return-timeline/return-timeline.component';

/**
 * Card herói do sócio (protótipo 01): à esquerda o selo do nível e a conclusão em uma frase, a linha do sócio e da
 * última declaração, CPF, regime, cônjuge e exposição à tomadora; à direita a linha do tempo das declarações, que
 * troca o ano em análise. Substitui a faixa navy e o card "Histórico de declarações".
 */
@Component({
  selector: 'app-pit-partner-header',
  standalone: true,
  imports: [VaultDatePipe, ReturnTimelineComponent],
  templateUrl: './partner-header.component.html',
  styleUrl: './partner-header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PartnerHeaderComponent {
  readonly partner = input.required<PartnerIncomeTaxPartnerDto>();
  /** Escolher outro ano-calendário na linha do tempo. */
  readonly selectYear = output<number>();

  protected readonly kindLabel = describeReturnKind;

  /** Nome do IRPF vem em maiúsculas: "Maria da Silva" na tela. */
  protected readonly displayName = computed(() => formatPersonName(this.partner().name));

  /** "99,9% do capital · Administrador" (a declaração e a data entram no template, com o pipe de data). */
  protected readonly roleLine = computed(() => {
    const p = this.partner();
    return [
      p.sharePercent != null ? `${formatSharePercent(p.sharePercent)} do capital` : null,
      p.role,
    ].filter(Boolean);
  });

  /** CPF mascarado, regime de bens e cônjuge, em linha pequena. */
  protected readonly identityLine = computed(() => {
    const p = this.partner();
    return [
      p.documentMasked ? `CPF ${p.documentMasked}` : null,
      p.maritalPropertyRegime,
      p.spouseName ? `cônjuge: ${formatPersonName(p.spouseName)}` : null,
    ]
      .filter(Boolean)
      .join(' · ');
  });

  /** Última declaração lida (a mais recente que não está só aguardando). */
  protected readonly latest = computed<ReturnSummaryDto | null>(
    () => this.partner().returns.find(r => r.status !== 'awaiting' && !!r.returnId) ?? null
  );

  protected readonly exposure = computed(() => {
    const ratio = this.partner().exposureToBorrower;
    return ratio == null
      ? null
      : `Exposição à tomadora: ${formatRatio(ratio)} do PL em quotas da própria empresa`;
  });

  /**
   * Conclusão do sócio: o primeiro ponto exibido (o backend já manda os pontos ordenados por gravidade) com o selo do
   * nível. Sem ponto exibido, a linha não aparece.
   */
  protected readonly conclusion = computed(() => {
    const top = visibleFindings(this.partner().findings, false)[0];
    if (!top) return null;
    const tag = FINDING_LEVEL_TAGS[top.level];
    return { css: tag.css, label: tag.label, title: top.title };
  });
}
